import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export const PageHero = ({ eyebrow, title, intro, image }) => (
  <section className="relative pt-[72px] bg-[#FAFAFC] overflow-hidden">
    <div className="hero-grid absolute inset-0" />
    <div
      className="absolute -top-40 right-[-10%] w-[560px] h-[560px] rounded-full opacity-[0.12] pointer-events-none"
      style={{ background: "radial-gradient(circle, #0FA89E 0%, transparent 65%)" }}
    />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 lg:pt-24 lg:pb-16">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-6"
      >
        {eyebrow}
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-[#0F172A] max-w-4xl"
      >
        {title}
      </motion.h1>
      {intro && (
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-2xl text-lg text-slate-600 leading-relaxed"
        >
          {intro}
        </motion.p>
      )}
    </div>
    {image && (
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
      >
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-[0_40px_80px_-30px_rgba(15,23,42,0.15)]">
          <img src={image} alt={title} className="w-full h-[240px] sm:h-[320px] lg:h-[400px] object-cover" />
        </div>
      </motion.div>
    )}
  </section>
);

export const PageCta = () => (
  <section className="py-16 lg:py-24 bg-[#0B0F19] grain relative overflow-hidden">
    <div
      className="absolute top-0 right-1/4 w-[420px] h-[420px] rounded-full opacity-[0.14] pointer-events-none"
      style={{ background: "radial-gradient(circle, #0FA89E 0%, transparent 65%)" }}
    />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-5">
        Stop paying for software that doesn't fit
      </h2>
      <p className="text-slate-400 text-lg max-w-xl mx-auto mb-9">
        Tell us what you're actually trying to do. A senior consultant replies
        within one business day. Fully booked for 2026 — accepting 2027 engagements.
      </p>
      <a
        href="/contact"
        data-testid="page-cta-contact"
        className="group inline-flex items-center gap-2.5 rounded-full bg-[#0FA89E] px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-[#0C8E85]"
      >
        Build my tool
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </a>
    </div>
  </section>
);
